import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { claimAmberwoodSplash } from './amberwoodSplashView';
import './AmberwoodSplash.css';
import type { AmberwoodSceneData } from './amberwoodSceneData';
import type { AmberwoodSiteMapProps } from './AmberwoodSiteMap';

export const AMBERWOOD_POSTER = '/img/campaigns/amberwood-at-holland.jpg';
export const AMBERWOOD_DURATION_MS = 10000;
export const AMBERWOOD_EXIT_MS = 550;
type LoadedScene = { SiteMap: React.ComponentType<AmberwoodSiteMapProps>; data: AmberwoodSceneData };
const sceneCache = new Map<string, Promise<LoadedScene | null>>();

/** One fetch per project per session, so a replay shows the model immediately. */
function loadAmberwoodScene(projectId: string): Promise<LoadedScene | null> {
  let pending = sceneCache.get(projectId);
  if (!pending) {
    pending = import('./AmberwoodSiteMap')
      .then(async module => {
        const data = await module.loadAmberwoodSceneData(projectId);
        return data ? { SiteMap: module.default, data } : null;
      })
      .catch(() => null)
      .then(scene => { if (!scene) sceneCache.delete(projectId); return scene; });
    sceneCache.set(projectId, pending);
  }
  return pending;
}

type Props = {
  onOpenProject: () => void;
  autoShow?: boolean;
  replayToken?: number;
  projectId?: string;
};

export default function AmberwoodSplash({
  onOpenProject,
  autoShow = true,
  replayToken = 0,
  projectId,
}: Props) {
  // Asked once per mount — StrictMode's effect re-run reuses the answer, since asking
  // again would record a second viewing.
  const claim = useRef<Promise<boolean> | null>(null);
  const [phase, setPhase] = useState<'playing' | 'exiting' | 'closed'>('closed');
  // The 3D panel holds its place (with a placeholder) until the model arrives, so a
  // slow load — typical for the auto splash while Super Picker itself is loading —
  // fades the model in rather than dropping it or shifting the layout.
  const [mapState, setMapState] = useState<'none' | 'pending' | LoadedScene>('none');
  const hideUnavailableMap = useCallback(() => setMapState('none'), []);

  useEffect(() => {
    let cancelled = false;
    let poster: HTMLImageElement | null = null;
    const open = () => {
      setMapState(projectId ? 'pending' : 'none');
      // Open straight away: the intro covers the first few seconds, so the poster
      // and the 3D model load behind it instead of delaying the splash.
      setPhase('playing');
      poster = new Image();
      poster.onerror = () => { if (!cancelled) setPhase('closed'); };
      poster.src = AMBERWOOD_POSTER;
      if (projectId) {
        void loadAmberwoodScene(projectId).then(scene => { if (!cancelled) setMapState(scene ?? 'none'); });
      }
    };
    if (replayToken > 0) open();
    else if (!autoShow) setPhase('closed');
    else {
      // At most twice per agent in 24h, 6h apart — the backend decides and records it.
      claim.current ??= claimAmberwoodSplash();
      void claim.current.then(show => { if (show && !cancelled) open(); });
    }
    return () => { cancelled = true; if (poster) poster.onerror = null; };
  }, [autoShow, replayToken, projectId]);

  useEffect(() => {
    if (phase !== 'playing' && phase !== 'exiting') return;
    const timer = window.setTimeout(
      () => setPhase(phase === 'playing' ? 'exiting' : 'closed'),
      phase === 'playing' ? AMBERWOOD_DURATION_MS : AMBERWOOD_EXIT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [phase]);

  const dismiss = () => setPhase('exiting');
  const openProject = () => {
    if (phase !== 'playing') return;
    setPhase('closed');
    onOpenProject();
  };

  return (
    <Modal open={phase === 'playing' || phase === 'exiting'} onClose={dismiss} aria-labelledby="amberwood-title">
      <div className={`amberwood-splash ${phase === 'exiting' ? 'amberwood-splash--exit' : ''}`} role="dialog" aria-modal="true" aria-labelledby="amberwood-title" tabIndex={-1}>
        <button className="amberwood-stage" onClick={openProject} aria-label="View Amberwood at Holland project" disabled={phase === 'exiting'}>
          <span className="amberwood-halo" aria-hidden="true" />
          <span className="amberwood-orbit amberwood-orbit--one" aria-hidden="true" />
          <span className="amberwood-orbit amberwood-orbit--two" aria-hidden="true" />
          <span className="amberwood-dust" aria-hidden="true">{Array.from({ length: 18 }, (_, i) => <i key={i} style={{ '--i': i } as React.CSSProperties} />)}</span>
          <span className="amberwood-intro">
            <span className="amberwood-eyebrow">A moment. A possibility.</span>
            <span className="amberwood-question">Have you considered</span>
            <span id="amberwood-title" className="amberwood-title">Amberwood</span>
            <span className="amberwood-location">AT HOLLAND</span>
            <span className="amberwood-rule" aria-hidden="true">✦</span>
          </span>
          <span className="amberwood-cta">Discover Amberwood <span aria-hidden="true">↗</span></span>
          <span className="amberwood-shimmer" aria-hidden="true" />
        </button>
        <div className={`amberwood-showcase ${mapState !== 'none' ? 'amberwood-showcase--with-map' : ''}`} aria-hidden="true">
          <div className="amberwood-poster">
            <img src={AMBERWOOD_POSTER} alt="" />
          </div>
          {mapState !== 'none' && (
            <>
              <span className="amberwood-divider">✦</span>
              <div className="amberwood-map">
                {mapState === 'pending'
                  ? <div className="amberwood-map-pending"><span>Preparing 3D view…</span></div>
                  : <mapState.SiteMap data={mapState.data} onUnavailable={hideUnavailableMap} />}
                <div className="amberwood-map-scrim" />
                <div className="amberwood-map-heading"><span>ANOTHER PERSPECTIVE</span><strong>Your next opportunity. In&nbsp;3D.</strong></div>
                <div className="amberwood-map-caption"><span className="amberwood-map-dot" />LIVE 360° SITE MAP
                  <span className="amberwood-map-legend"><i className="amberwood-map-key--open" />Available<i className="amberwood-map-key--sold" />Sold</span>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="amberwood-topline"><span>HUTTONS <b>/</b> SUPER PICKER</span><button onClick={dismiss} aria-label="Skip Amberwood introduction">Skip <span aria-hidden="true">×</span></button></div>
        <div className="amberwood-footer">
          <span>Tap to explore the project</span>
        </div>
        <span className="amberwood-progress" aria-hidden="true" />
      </div>
    </Modal>
  );
}
